import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../CSS/Global.css';
import './CustomerStyle.css';
import { useSidebar } from '../../SidebarContext';
import Header from '../Header';
import Sidebar from '../SideBar';
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Mail, Phone } from 'lucide-react';
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { decryptToken } from '../utils/getToken';

const Customer = () => {
  const { isSidebarOpen } = useSidebar();
  const navigate = useNavigate();
  const { globalSearch } = useSearch();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const url = process.env.REACT_APP_LINK
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
  const [load, setLoad] = useState(false)
  const token = decryptToken("accessToken") || ""

  const fetchCustomers = async () => {
    try {
      setLoad(true); // Start loading
      const response = await fetch(`${url}/api/customer/all/${isLoggedIn.main_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const data = await response.json();
      const parsedCustomers = data.map((customer) => ({
        ...customer,
        billingAddress: customer.billingAddress ? JSON.parse(customer.billingAddress) : {},
        shippingAddress: customer.shippingAddress ? JSON.parse(customer.shippingAddress) : {},
        contact: customer.contact ? JSON.parse(customer.contact) : [],
      }));
      console.log("Customer data", data);
      setCustomers(parsedCustomers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoad(false); // Stop loading after everything is done
    }
  };


  useEffect(() => {
    fetchCustomers();
  }, []);

  // 1. Filter by date
  let processedCustomers = [...customers];

  if (customDate.from || customDate.to) {
    processedCustomers = processedCustomers.filter((customer) => {
      const createdAt = new Date(customer.createdAt);
      const from = customDate.from ? new Date(customDate.from) : null;
      const to = customDate.to ? new Date(customDate.to) : null;

      return (!from || createdAt >= from) && (!to || createdAt <= to);
    });
  }

  // 2. Sort by createdAt
  processedCustomers.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });


  const filteredCustomers = processedCustomers.filter((customer) =>
    (customer.customerName || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
    (customer.companyName || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
    (customer.email || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
    (customer.phone1 || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
    (customer.gstNumber || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
    (customer.remarks || '').toLowerCase().includes(globalSearch.toLowerCase())
  );

  const indexOfLastCustomer = currentPage * entriesPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / entriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleSearch = (event) => setSearchQuery(event.target.value);
  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen);
  const toggleImport = () => setImportOpen(!isImportOpen);
  const handleRefresh = () => {
    setLoading(true);
    fetchCustomers();
    setTimeout(() => setLoading(false), 1000);
  };

  const handleDelete = async (customerId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This customer will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${url}/api/customer/${customerId}`, {
            method: 'DELETE',
          });

          if (!response.ok) {
            const err = await response.json();
            throw new Error(err?.message || 'Failed to delete');
          }

          Swal.fire('Deleted!', 'Customer has been deleted.', 'success');
          setCustomers(customers.filter(c => c.id !== customerId));
        } catch (error) {
          console.error('Delete error:', error.message);
          Swal.fire('Error!', `Failed to delete customer. ${error.message}`, 'error');
        }
      }
    });
  };


  const handleEdit = (customer) => {
    navigate('/edit-customer', { state: customer });

  };

  const SkeletonLoader = () => (
    <div>
      {Array(10).fill('').map((_, index) => (
        <div className="skeleton-row" key={index}>
          {[...Array(5)].map((_, i) => <div className="skeleton" key={i}></div>)}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Loader />
    );
  }
  if (load) {
    return (
      <Loader />
    );
  }

  return (
    <>

      <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="customers-header">
          <div className="customer-dropdown">
            <p className="dashboard-title" onClick={toggleDropdown}>
              All Customers
              {/* <i className={`fa-solid fa-chevron-down ${isDropdownOpen ? 'rotate' : ''}`}></i> */}
            </p>
          </div>

          <div className="customer-warning-new-hamburger-help">
            <p className="customer-warning-text">
              <span><i className="fa-solid fa-triangle-exclamation"></i> Update GST details</span> for 520 customers
            </p>
            <div className="customer-new">
              <Link to='/add-customer'>
                <button className="oms-add-btn"><i className="fa-solid fa-plus"></i> New</button>
              </Link>
            </div>
            <div className="filter-controls">
              <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
                <i className="fa-solid fa-sliders"></i> Filters
              </button>
            </div>
            <div className="import-hamburger" onClick={toggleImport}>
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </div>
            {isImportOpen && (
              <div className="import-ul">
                <ul>
                  <ul>
                    <li><Link to='/customer-import'><i className="fa-solid fa-file-arrow-down"></i> Import Customers</Link></li>
                  </ul>
                  <ul>
                    <li><Link to='/export-customer'><i className="fa-solid fa-file-arrow-up"></i> Export Customers</Link></li>
                  </ul>
                  <ul>
                    <li onClick={handleRefresh}><i className="fa-solid fa-rotate"></i> Refresh List</li>
                  </ul>
                  <ul>
                    <li>
                      <a
                        href="/sample_customer.xlsx"
                        download
                        style={{ textDecoration: "none" }}
                      >
                        <i className="fa-solid fa-file-arrow-down"></i> Download Sample
                      </a>

                    </li>
                  </ul>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* <div className="filter-search">
          <div className="filter-search-input">
            <input type="search" placeholder="Search..." value={searchQuery} onChange={handleSearch} />
            <i className="fa fa-search"></i>
          </div>
        </div> */}

        {/* <div className="customer-table">
          {loading ? (
            <SkeletonLoader />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company Name</th>
                  <th>Email</th>
                  <th>Work Phone</th>
                  <th>GST Number</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {
                  currentCustomers.length > 0 ? (

                    currentCustomers.map((customer, index) => (
                      <tr key={index}>
                        <td>{customer.customerName}</td>
                        <td>{customer.companyName}</td>
                        <td>{customer.email}</td>
                        <td>{customer.phone1}</td>
                        <td>{customer.gstNumber}</td>
                        <td>{new Date(customer.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                        <td>
                          <button onClick={() => handleEdit(customer)} className="edit-btn">
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button onClick={() => handleDelete(customer.id)} className="delete-btn" style={{ marginLeft: '8px' }}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center' }}>No data found.</td>
                    </tr>
                  )}
              </tbody>
            </table>
          )}
        </div> */}
        {
          currentCustomers.length > 0 ? (
            currentCustomers.map((customer, index) => (
              <div
                className="ecom-customer-card"
                key={customer.id || index}
                onClick={() => navigate(`/customer-detail/${customer.id}`)}
                style={{ cursor: "pointer" }}
              >
                {customer.customerType && (
                  <div className="ecom-customer-time">
                    {customer.customerType}
                  </div>
                )}
                <>
                  {customer.customerName && (
                    <h2 className="ecom-customer-name">{customer.customerName}-{customer.cust_ID}</h2>
                  )}
                  {/* {
                    customer.customer_id && (
                      <div className="ecom-customer-row">

                        <span>{customer.customer_id}</span>
                      </div>
                    )
                  } */}
                </>
                {customer.phone1 && (
                  <div className="ecom-customer-row">
                    <Phone className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.phone1}</span>
                  </div>
                )}


                {customer.email && (
                  <div className="ecom-customer-row">
                    <Mail className="ecom-customer-icon" strokeWidth={2} />
                    <span>{customer.email}</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p style={{ textAlign: "center", margin: "50px" }}>No customers found.</p>
          )
        }



        {totalPages > 1 && (

          <div className="pagination">
            {/* First Page */}
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronsLeft size={18} />
            </button>

            {/* Previous Page */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-chev-btn"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Current Page Number Only */}
            <button className="pagination-btn active">
              {currentPage}
            </button>

            {/* Next Page */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronRight size={18} />
            </button>

            {/* Last Page */}
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-chev-btn"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}

      </div>
      <FilterDrawer />
    </>
  );
};

export default Customer;
